import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/solid'
import GenericSingleSelector from 'components/DropdownSelectors/GenericSingleSelector'
import InputSpinner from 'components/Spinners/input'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { AuthContext, useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { Fragment, useEffect, useState } from 'react'
import { denormalizeLocation } from 'services/locations'
import { Integrations } from 'types/integration'
import { LocationItem } from 'types/location'
import { MembershipApiItem } from 'types/membership'
import { classNames } from 'utils/dom-helpers'
import { useApiRequest, useFetch } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { toCurrency } from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'
import { createModal } from '../../ModalFactory'
import { ModalWrapper } from '../../ModalWrapper'

export type StripeCard = {
    last4: string
}

const ModalProvider = createModal(
    AuthContext,
    'MembershipAddModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <Translation
                render_as="span"
                className="circular"
                content_key="add_membership"
            />
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-x " />
        </span>
    )
)

export const MembershipAddModalOpener = ModalProvider.Opener
export const MembershipAddModalCloser = ModalProvider.Closer

export function MembershipAddModal() {
    const { getApiRequest } = useApiRequest()
    const {
        CreateClientModal,
        MembershipAddModal,
        PurchaseSummaryModal,
    } = useAuth()
    const { tenant } = useTenantContext()
    const { translations } = useAppContext()
    const [locations, setLocations] = useState<LocationItem[]>([])
    const [selected_location, setSelectedLocation] =
        useState<LocationItem>()
    const [memberships, setMemberships] = useState<MembershipApiItem[]>([])

    const [selected_membership, setSelectedMembership] =
        useState<MembershipApiItem>()
    const [stripe_card, setStripeCard] = useState<StripeCard>()
    const [loading_card, setLoadingCard] = useState<boolean>(true)
    const [purchase_enabled, togglePurchaseButton] =
        useState<boolean>(false)
    const [display_cash_checkbox, toggleDisplayCashCheckbox] = useState(false)
    const [cash_payment, toggleCashPayment] = useState(false)
    const [filter, setFilter] = useState<string | undefined>()
    const [packages, setPackages] = useState<MembershipApiItem[] | undefined>()
    const [subscriptions, setSubscriptions] = useState<MembershipApiItem[] | undefined>()

    const client_id = CreateClientModal.attributes?.id as string

    useEffect(() => {
        if (!MembershipAddModal.is_open) {
            setSelectedLocation(undefined)
            setSelectedMembership(undefined)
            setStripeCard(undefined)
            toggleCashPayment(false)
            togglePurchaseButton(false)
            setLoadingCard(true)
            setFilter(undefined)
        }
    }, [MembershipAddModal.is_open])

    const { data: xhr_locations, status: locations_api_status } = useFetch(
        `/v1/locations/tenant-id?size=500&tenantId=${tenant?.id}&isDeleted=false`,
        FetchMethods.GET,
        !!tenant?.id && MembershipAddModal.is_open
    )

    const {
        data: memberships_api_response,
        status: membership_api_status,
    } = useFetch(
        `/v1/memberships/by-tenant-id?tenantId=${tenant?.id}`,
        FetchMethods.GET,
        !!tenant?.id && MembershipAddModal.is_open
    )

    const { data: integration_api_response } = useFetch(
        `/v1/integrations/category-type?categoryType=INTEGRATION`,
        FetchMethods.GET,
        MembershipAddModal.is_open
    )

    // Locations
    useEffect(() => {
        if (locations_api_status == 200) {
            let fetched_locations =
                xhr_locations.content.map(denormalizeLocation)
            fetched_locations.unshift({
                name: 'All Locations',
                id: '',
            })
            setLocations(fetched_locations)
            setSelectedLocation(fetched_locations[0])
        }
    }, [xhr_locations, locations_api_status])

    // Memberships
    useEffect(() => {
        if (membership_api_status == 200) { //TODO: refactor and move to the tenant context
            setMemberships(memberships_api_response || [])
        }
    }, [memberships_api_response, membership_api_status])

    // Integrations
    useEffect(() => {
        if (integration_api_response?.length && tenant?.id && client_id) {
            let stripe = integration_api_response.filter(
                (i: any) => i.name == Integrations.STRIPE
            )[0]
            if (stripe) {
                let api = getApiRequest(
                    `/v1/payments/client-payment-method?tenantId=${tenant?.id}&clientId=${client_id}`
                )
                api.then((payment_api_response) => {
                    if (payment_api_response) {
                        let card: StripeCard = {
                            last4: payment_api_response.cardLast4,
                        }
                        setStripeCard(card)
                        setLoadingCard(false)
                    }
                })
            }
            else {
                setLoadingCard(false)
            }
        }
    }, [integration_api_response, tenant?.id, client_id])

    useEffect(() => {
        if (selected_location && memberships) {
            let filtered_memberships
            let regex: RegExp | undefined
            if (filter) {
                regex = new RegExp(filter, 'i')
            }
            if (selected_location.name == 'All Locations') {
                if (regex != undefined) {
                    filtered_memberships = memberships.filter((mem) => regex!.test(mem.name))
                } else {
                    filtered_memberships = memberships
                }
            } else {
                filtered_memberships = memberships.filter(
                    (i) =>
                        !i.locationIds ||
                        i.primaryLocationId == selected_location.id &&
                        (regex != undefined ? regex.test(i.name) : true)
                )
            }
            let pacs: MembershipApiItem[] = [], subs: MembershipApiItem[] = []
            filtered_memberships.forEach((mem) => {
                if (mem.membershipPackage) {
                    pacs.push(mem)
                } else if (mem.membershipSubscription) {
                    subs.push(mem)
                }
            })
            setPackages(pacs?.length > 0 ? pacs : undefined)
            setSubscriptions(subs?.length > 0 ? subs : undefined)
            setSelectedMembership(filtered_memberships[0])
        }
    }, [selected_location, memberships, filter])

    // Cash Checkbox
    useEffect(() => {
        if (selected_membership?.membershipPackage) {
            toggleDisplayCashCheckbox(true)
        }
        else {
            toggleDisplayCashCheckbox(false)
            toggleCashPayment(false)
        }
    }, [selected_membership])

    // Purchase Button
    useEffect(() => {
        if (stripe_card?.last4 || cash_payment) {
            togglePurchaseButton(true)
        } else {
            togglePurchaseButton(false)
        }
    }, [stripe_card, cash_payment])

    const handleMembershipChange = (membership_id: string) => {
        const selected_membership = memberships.find(
            (item) => item.id === membership_id
        )
        setSelectedMembership(selected_membership)
    }

    const handlePayment = () => {
        if (!selected_membership) {
            return
        }

        const primary_location = locations.find(
            (l) =>
                l.id == selected_membership.primaryLocationId
        ) || locations[0]

        PurchaseSummaryModal.setAttributes({
            primary_location: primary_location,
            selected_membership: selected_membership,
            is_cash_payment: cash_payment,
        })

        PurchaseSummaryModal.open()
        MembershipAddModal.close()
    }

    function membershipItemDisplay(membership?: MembershipApiItem) {
        let value: string
        let type: string

        if (membership?.redemptionAmount) {
            value = toCurrency(membership.redemptionAmount)
            type = "common.credits"
        } else if (membership?.redemptionMinutes) {
            value = (membership.redemptionMinutes / 60).toFixed(2) as unknown as string
            type = "common.hours"
        } else {
            value = (membership?.redemptionServices || 0) as unknown as string
            type = "common.classes"
        }

        return (
            <Menu.Item key={membership?.name}>
                {({ active }) => (
                    <div
                        className={classNames(
                            active
                                ? 'bg-[#86C5E0]/10'
                                : 'bg-white',
                            'flex flex-row items-center justify-between p-2 font-[450] rounded-lg whitespace-normal',
                        )}
                        onClick={() => {
                            setSelectedMembership(membership)
                        }}
                    >
                        <span className='text-sm text-neutral-700'>{membership?.name}</span>
                        <span className='text-xs text-zinc-500'>
                            {value}
                            &nbsp;
                            <Translation
                                content_key={type}
                            />
                        </span>
                    </div>
                )}
            </Menu.Item>
        )
    }

    return (
        <ModalProvider.Visible>
            <ModalWrapper className="w-auto">
                <div className="bg-white shadow-2xl overflow-clip sm:rounded-md relative border-2 border-gray-100 my-auto">
                    <div className="flex flex-col text-gray-500 z-10 w-full p-4">
                        <MembershipAddModalCloser className="text-right text-2xl" />
                        <Translation
                            render_as="span"
                            className="circular inline-block self-center text-2xl font-medium text-primary-dark mx-auto"
                            content_key="add_membership"
                        />{' '}
                        <Translation
                            render_as="span"
                            className="self-center"
                            content_key="add_membership_subtitle"
                        />
                    </div>
                    <div className="flex flex-col gap-y-10 w-full p-10 text-black">

                        <div className="flex flex-col gap-2">
                            <label
                                htmlFor="name"
                                className={classNames(
                                    'block text-base circular font-[450] text-stone-700'
                                )}
                            >
                                <Translation content_key="select_location" render_as='h3' className='font-[450] text-neutral-700' />
                            </label>
                            <GenericSingleSelector
                                items={locations}
                                selected_item={selected_location}
                                titleDisplay={(item: LocationItem) => {
                                    return (
                                        <div className="flex flex-row w-full p-1 items-center">
                                            <i className="feather-map-pin text-xl text-secondary-blue" />
                                            <span className='ml-3.5 flex-grow text-base font-[450] text-darl'>
                                                {item?.name}
                                            </span>
                                            <ChevronDownIcon
                                                className="-mr-1 ml-2 h-5 w-5"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    )
                                }}
                                itemDisplay={(item: LocationItem, index: number, is_selected: boolean) => {
                                    return (
                                        <Menu.Item key={item.name}>
                                            {({ active }) => (
                                                <div
                                                    className={classNames(
                                                        active
                                                            ? 'bg-[#86C5E0]/10'
                                                            : 'bg-white',
                                                        'flex flex-row text-xs items-center font-medium p-2 rounded-lg whitespace-normal',
                                                    )}
                                                    onClick={() => {
                                                        setSelectedLocation(item)
                                                    }}
                                                >
                                                    &nbsp;
                                                    {item?.name}
                                                </div>
                                            )}
                                        </Menu.Item>
                                    )
                                }}
                                isItemSelected={(item: LocationItem, selected_item: LocationItem) => {
                                    return item?.id === selected_item?.id
                                }}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Translation
                                render_as="p"
                                content_key="available_memberships"
                                className="circular font-normal text-stone-700"
                            />

                            <Menu
                                as="div"
                                className='relative inline-block text-left'
                            >
                                {({ open }) => (
                                    <>
                                        <Menu.Button
                                            className={classNames(
                                                'inline-flex text-left items-center w-full',
                                                'rounded-md border border-gray-150 shadow-sm px-4 py-2 bg-white',
                                                'text-sm font-medium text-gray-700 disabled:bg-gray-100 disabled:hover:bg-gray-200 disabled:cursor-not-allowed hover:bg-gray-50 ',
                                            )}
                                        >
                                            <div className="flex flex-row w-full p-1 items-center text-black-one">
                                                {open
                                                    ?
                                                    <input
                                                        autoFocus
                                                        value={filter}
                                                        onChange={(e) => {
                                                            setFilter(e.target.value)
                                                        }}
                                                        className="flex w-full bg-inherit text-base"
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                        }}
                                                        placeholder={getTranslation("memberships.membership_search_placeholder", translations)}
                                                        onKeyDown={(e) => {
                                                            if (e.key == ' ') {
                                                                e.preventDefault()
                                                                setFilter(filter + ' ')
                                                            }
                                                        }}
                                                    />
                                                    :
                                                    <span className='flex-grow text-base font-[450] text-dark'>
                                                        {selected_membership?.name}
                                                    </span>
                                                }
                                                <ChevronDownIcon
                                                    className="-mr-1 ml-2 h-5 w-5"
                                                    aria-hidden="true"
                                                />

                                            </div>
                                        </Menu.Button>
                                        <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-100"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                            show={open}
                                        >
                                            <Menu.Items
                                                static
                                                className={classNames(
                                                    'absolute mt-2 w-full',
                                                    'rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5',
                                                    'focus:outline-none z-10'
                                                )}
                                            >
                                                <div className="p-2 overflow-y-auto max-h-[300px]">
                                                    <Menu.Item>
                                                        <Translation
                                                            content_key='packages'
                                                            render_as='div'
                                                            className='flex flex-row text-xs items-center font-bold text-primary mb-2.5 rounded-lg whitespace-normal uppercase'
                                                        />
                                                    </Menu.Item>
                                                    {packages
                                                        ?.filter((i) => i.membershipPackage)
                                                        .map(
                                                            (
                                                                i: any,
                                                                index
                                                            ) => {
                                                                return (
                                                                    membershipItemDisplay(i)
                                                                )
                                                            }
                                                        )
                                                        ||
                                                        <option
                                                            disabled
                                                            className="text-primary font-bold text-xs"
                                                        >
                                                            {getTranslation(
                                                                'no_packages_found',
                                                                translations
                                                            )}
                                                        </option>
                                                    }
                                                    <div className='border border-gray-three w-full my-2.5' />
                                                    <Menu.Item>
                                                        <Translation
                                                            content_key='subscriptions'
                                                            render_as='div'
                                                            className='flex flex-row text-xs items-center font-bold text-primary mb-2.5 rounded-lg whitespace-normal uppercase'
                                                        />
                                                    </Menu.Item>
                                                    {subscriptions
                                                        ?.filter((i) => i.membershipSubscription)
                                                        .map(
                                                            (
                                                                i: MembershipApiItem
                                                            ) => {
                                                                return (
                                                                    membershipItemDisplay(i)
                                                                )
                                                            }
                                                        )
                                                        ||
                                                        <option
                                                            disabled
                                                            className="text-primary font-bold text-xs"
                                                        >
                                                            {getTranslation(
                                                                'no_packages_found',
                                                                translations
                                                            )}
                                                        </option>
                                                    }
                                                </div>
                                            </Menu.Items>
                                        </Transition>
                                    </>
                                )}
                            </Menu>
                        </div>
                        {display_cash_checkbox && (
                            <div className="ml-1">
                                <input
                                    defaultChecked={cash_payment}
                                    id="check"
                                    className="border-gray-400 rounded-sm mr-3  focus:ring-0"
                                    type="checkbox"
                                    onChange={(e) =>
                                        toggleCashPayment(
                                            e.target.checked
                                        )
                                    }
                                />
                                <label htmlFor="check">
                                    <Translation content_key="received_cash_payment" />
                                </label>
                            </div>
                        )}
                        {!cash_payment && (
                            <div className="flex flex-col gap-2">
                                <Translation
                                    render_as="p"
                                    content_key="payment"
                                />
                                <div className="flex items-center">
                                    <div
                                        className={classNames(
                                            'bg-gray-300 bg-opacity-50 rounded-md px-4 py-2 mr-2',
                                            stripe_card?.last4
                                                ? 'opacity-100'
                                                : 'opacity-50'
                                        )}
                                    >
                                        <i className="feather-credit-card text-primary text-xl mr-2 align-middle" />
                                        {stripe_card?.last4 ? (
                                            <Translation
                                                render_as="span"
                                                className="align-middle"
                                                content_key="pay_with_last4"
                                                content_values={{
                                                    last4: stripe_card?.last4,
                                                }}
                                            />
                                        ) : (
                                            <Translation
                                                render_as="span"
                                                className="align-middle "
                                                content_key="pay_with_client_details"
                                            />
                                        )}
                                    </div>
                                    <InputSpinner loading={loading_card} />
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col">
                            <div className="flex justify-between mx-auto">
                                <button
                                    type="button"
                                    className="border border-gray-300 rounded-lg inline-block mr-3 py-3 px-8"
                                    onClick={() => {
                                        MembershipAddModal.close()
                                    }}
                                >
                                    <Translation content_key="cancel" />
                                </button>

                                <button
                                    type="button"
                                    disabled={!purchase_enabled}
                                    className={classNames(
                                        'group flex justify-center',
                                        'py-3 px-8 border border-transparent',
                                        'rounded-md text-white',
                                        'bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                        purchase_enabled
                                            ? 'opacity-100'
                                            : 'opacity-50'
                                    )}
                                    onClick={handlePayment}
                                >
                                    <Translation content_key="purchase_membership" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default MembershipAddModal
